import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getCompanies,
  getCompanyRules,
  operatorUpdateCompanyRules,
} from '../../../api/General/Company';
import {
  AttendanceImageConfig,
  FaceRecognitionConfig,
  ShiftFlowRuleType,
  CompanyRulesInput,
  CompanyRulesView,
  CompanyView,
  TriggerOvertimeVerificationOn,
  BreakConfig,
} from '../../../models/general';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { PageLayout } from '../../../components/ui/PageLayout';
import { ScrollText } from 'lucide-react';
import { getApiErrorMessage } from '../../../utils/apiErrors';

type EnumLike = { [key: string]: string | number };

const enumOptions = (e: EnumLike, t: (key: string) => string) => {
  const values = Object.values(e).filter(v => typeof v === 'number');
  const enumValues = values.length > 0 ? values : Object.values(e);
  return enumValues.map(v => {
    const name = typeof v === 'number' ? Object.keys(e).find(k => e[k] === v) || v.toString() : v.toString();
    return { value: v, label: t(name) };
  });
};

interface EnumSelectProps<T> {
  label: string;
  enumType: EnumLike;
  value: T;
  onChange: (v: T) => void;
  t: (key: string) => string;
}

const EnumSelect = <T,>({ label, enumType, value, onChange, t }: EnumSelectProps<T>) => {
  const options = useMemo(() => enumOptions(enumType, t), [enumType, t]);
  const currentValue = useMemo(() => {
    const match = options.find(o => o.value === value || o.value?.toString() === (value as unknown as string)?.toString());
    return match?.value;
  }, [options, value]);

  return (
    <div className="flex flex-col gap-1.5">
      <Select
        label={label}
        options={options}
        value={currentValue}
        onChange={(v: string | number) => onChange(v as T)}
      />
    </div>
  );
};

interface NumberFieldProps {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  placeholder?: string;
  min?: number;
  suffix?: string;
}

const NumberField = ({ label, value, onChange, placeholder, min, suffix }: NumberFieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-sm font-medium text-foreground">{label}</label>
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={value ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = e.target.value;
          onChange(val === '' ? null : Number(val));
        }}
        placeholder={placeholder}
        min={min}
        className="w-full"
      />
      {suffix && <span className="text-xs text-muted-foreground whitespace-nowrap">{suffix}</span>}
    </div>
  </div>
);

interface CheckFieldProps {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

const CheckField = ({ label, checked, onChange }: CheckFieldProps) => (
  <Checkbox
    label={label}
    checked={checked}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
  />
);

const initialState: CompanyRulesInput = {
  id: 0,
  attendanceImageConfig: AttendanceImageConfig.Required,
  faceRecognitionConfig: FaceRecognitionConfig.Required,
  shiftFlowRuleType: ShiftFlowRuleType.Default,
  timesheetCreationDay: 0,
  hideClockingImagesFromTimesheet: false,
  disableAllNotifications: false,
  faceRecognitionEnabled: false,
  companyId: 0,
  triggerOvertimeVerificationOn: TriggerOvertimeVerificationOn.ShiftWorkingHoursFullfiled,
  breakConfig: BreakConfig.Required,
  scheduledShiftClockInReminderOffsetMinutes: 15,
  scheduledShiftClockOutReminderOffsetMinutes: 15,
  scheduledShiftEarlyClockInWindowMinutes: 120,
  scheduledShiftAbsentThresholdPercent: 0,
  scheduledShiftHalfDayThresholdPercent: 50,
  scheduledShiftLateClockInThresholdMinutes: 15,
  scheduledShiftEarlyClockOutThresholdMinutes: 15,
  breakInReminderMinutesAfterClockIn: null,
  breakOutReminderMinutesAfterBreakIn: 45,
};

// 🔴 CO-240 / owner decision §14.1c/14 — engagement-family flags this page must NEVER write.
//
// GET /Company/GetCompanyRules returns a CompanyRulesView in which all four of these are
// NON-NULLABLE bools (CompanyRulesView.cs — EnableRecognitionGapReminder / EnableCheckInReminder /
// EnableQ12Nudge / EnableAINudges). This page spreads that whole view into its editor state and
// POSTs the whole state back, so the flags always materialised into the body as real booleans.
// CompanyService.UpdateCompanyRules (CompanyService.cs, `if (input.EnableXxx.HasValue)`) only
// protects editors that OMIT the field — a materialised `false` is indistinguishable from a
// deliberate one, so this page was silently writing them on every save, for ANY tenant it is
// pointed at.
//
// This page is cross-tenant, so the collision is not hypothetical: an Operator saves an unrelated
// attendance rule here for company X at 10:10 and reverts whatever the Engagement Jobs surface set
// for company X at 10:05, with no signal to either writer.
//
// These are not this page's settings — no control below renders or edits them. Stripping them from
// the write restores the server's HasValue guard to doing what it was written to do. Owned by the
// Engagement Jobs surfaces (Settings > Notifications > Engagement Jobs, and the Operator
// Engagement Jobs page).
const ENGAGEMENT_FLAG_KEYS = [
  'enableRecognitionGapReminder',
  'enableCheckInReminder',
  'enableQ12Nudge',
  'enableAINudges',
  // 🔴 SAME CLASS, FIFTH MEMBER — found by an adversarial pass after the first four were stripped.
  // CO-354's asset-replenishment flag has the identical shape: `bool?` on the input behind a
  // HasValue guard (CompanyService.cs, `if (input.EnableAssetReplenishmentNotification.HasValue)`)
  // but NON-nullable `bool` on the view (CompanyRulesView.cs; client general.ts), so the spread
  // materialises it as a real boolean and this page writes it on every save. It is owned by
  // Assets > Replenishment (ReplenishmentTab.tsx:160), which is the only surface that edits it.
  // Stripping four of five would have left the defect live under a comment claiming it was fixed.
  'enableAssetReplenishmentNotification',
] as const;

// Returns a copy with the engagement flags REMOVED (not nulled). Axios serialises with
// JSON.stringify and the server deserialises with Newtonsoft (Program.cs AddNewtonsoftJson,
// NullValueHandling.Ignore), so an absent key leaves the bool? at null and the HasValue guard
// skips it.
const withoutEngagementFlags = <T extends Partial<CompanyRulesInput>>(payload: T): T => {
  const out = { ...payload };
  for (const key of ENGAGEMENT_FLAG_KEYS) delete out[key];
  return out;
};

export default function OperatorCompanyRules() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast, showToast, dismissToast } = useToast();

  const [companies, setCompanies] = useState<CompanyView[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [input, setInput] = useState<CompanyRulesInput>(initialState);
  const [rulesView, setRulesView] = useState<CompanyRulesView | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Load companies list
  useEffect(() => {
    (async () => {
      try {
        const res = await getCompanies();
        if (res.errors?.length) {
          showToast(t('Failed to load companies'), 'error');
        } else {
          setCompanies(res.payload ?? []);
        }
      } catch (e) {
        console.error(e);
        showToast(t('Failed to load companies'), 'error');
      } finally {
        setLoadingCompanies(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select from query param
  useEffect(() => {
    const qp = searchParams.get('companyId');
    if (qp && companies.length > 0) {
      const id = Number(qp);
      if (companies.some(c => c.id === id)) {
        setSelectedCompanyId(id);
      }
    }
  }, [searchParams, companies]);

  // Load rules when company changes
  const fetchRules = useCallback(async (companyId: number) => {
    setLoading(true);
    try {
      const res = await getCompanyRules(companyId);
      if (res.errors?.length) {
        showToast(t('Failed to load rules'), 'error');
      } else {
        setInput(prev => ({ ...prev, ...res.payload, companyId }));
        setRulesView(res.payload);
      }
    } catch (e) {
      console.error(e);
      showToast(t('Failed to load rules'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  useEffect(() => {
    if (selectedCompanyId) {
      fetchRules(selectedCompanyId);
    }
  }, [selectedCompanyId, fetchRules]);

  const handleCompanyChange = (val: number | string) => {
    const id = Number(val);
    setSelectedCompanyId(id);
    setSearchParams({ companyId: String(id) });
  };

  const submit = async () => {
    if (!selectedCompanyId) return;
    setSaving(true);
    try {
      // Engagement flags are stripped: they ride the fetched view but belong to the Engagement
      // Jobs surfaces. See ENGAGEMENT_FLAG_KEYS above.
      const res = await operatorUpdateCompanyRules(selectedCompanyId, withoutEngagementFlags(input));
      if (res.errors?.length) {
        showToast(getApiErrorMessage(res, t, t('Update failed')), 'error');
      } else {
        showToast(t('Company Rules Updated Successfully'), 'success');
        // Refetch to update computed fields (hasCustomAIKey, hasWialonToken)
        await fetchRules(selectedCompanyId);
      }
    } catch (e) {
      console.error(e);
      showToast(getApiErrorMessage(e, t, t('Update failed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const companyOptions = useMemo(
    () => companies.map(c => ({ value: c.id, label: c.name })),
    [companies]
  );

  return (
    <PageLayout
      title={t('Company Rules Management')}
      description={t('Select a company and configure its attendance, notification, and system rules')}
      icon={ScrollText}
      accentColor="slate"
      maxWidth="5xl"
    >
      <div className="flex flex-col gap-6">
      {/* Company Selector */}
      <div className="max-w-sm">
        {loadingCompanies ? (
          <Spinner size="md" />
        ) : (
          <Select
            label={t('Select Company')}
            options={companyOptions}
            value={selectedCompanyId}
            onChange={handleCompanyChange}
            searchable
            placeholder={t('Choose a company...')}
          />
        )}
      </div>

      {/* Rules Form */}
      {selectedCompanyId && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {/* Section: Attendance */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Attendance')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <EnumSelect
                    label={t('Attendance Image Rule')}
                    enumType={AttendanceImageConfig}
                    value={input.attendanceImageConfig}
                    onChange={(v) => setInput(i => ({ ...i, attendanceImageConfig: v as AttendanceImageConfig }))}
                    t={t}
                  />
                  <EnumSelect
                    label={t('Shift Flow Rule Type')}
                    enumType={ShiftFlowRuleType}
                    value={input.shiftFlowRuleType}
                    onChange={(v) => setInput(i => ({ ...i, shiftFlowRuleType: v as ShiftFlowRuleType }))}
                    t={t}
                  />
                  <EnumSelect
                    label={t('Trigger Overtime Verification On')}
                    enumType={TriggerOvertimeVerificationOn}
                    value={input.triggerOvertimeVerificationOn}
                    onChange={(v) => setInput(i => ({ ...i, triggerOvertimeVerificationOn: v as TriggerOvertimeVerificationOn }))}
                    t={t}
                  />
                  <EnumSelect
                    label={t('Break Configuration')}
                    enumType={BreakConfig}
                    value={input.breakConfig}
                    onChange={(v) => setInput(i => ({ ...i, breakConfig: v as BreakConfig }))}
                    t={t}
                  />
                </div>
              </section>

              {/* Section: Notification Timing */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Notification Timing')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <NumberField
                    label={t('Clock-In Reminder Offset')}
                    value={input.scheduledShiftClockInReminderOffsetMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftClockInReminderOffsetMinutes: v ?? 15 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Clock-Out Reminder Offset')}
                    value={input.scheduledShiftClockOutReminderOffsetMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftClockOutReminderOffsetMinutes: v ?? 15 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Clock-In Reminder Before')}
                    value={input.scheduledShiftClockInReminderBeforeMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftClockInReminderBeforeMinutes: v }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Clock-Out Reminder Before')}
                    value={input.scheduledShiftClockOutReminderBeforeMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftClockOutReminderBeforeMinutes: v }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Early Clock-In Window')}
                    value={input.scheduledShiftEarlyClockInWindowMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftEarlyClockInWindowMinutes: v ?? 120 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Break-In Reminder After Clock-In')}
                    value={input.breakInReminderMinutesAfterClockIn}
                    onChange={(v) => setInput(i => ({ ...i, breakInReminderMinutesAfterClockIn: v }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Break-Out Reminder After Break-In')}
                    value={input.breakOutReminderMinutesAfterBreakIn}
                    onChange={(v) => setInput(i => ({ ...i, breakOutReminderMinutesAfterBreakIn: v ?? 45 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                </div>
              </section>

              {/* Section: Attendance Thresholds */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Attendance Thresholds')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <NumberField
                    label={t('Absent Threshold')}
                    value={input.scheduledShiftAbsentThresholdPercent}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftAbsentThresholdPercent: v ?? 0 }))}
                    suffix={t('%')}
                    min={0}
                  />
                  <NumberField
                    label={t('Half-Day Threshold')}
                    value={input.scheduledShiftHalfDayThresholdPercent}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftHalfDayThresholdPercent: v ?? 50 }))}
                    suffix={t('%')}
                    min={0}
                  />
                  <NumberField
                    label={t('Late Clock-In Threshold')}
                    value={input.scheduledShiftLateClockInThresholdMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftLateClockInThresholdMinutes: v ?? 5 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                  <NumberField
                    label={t('Early Clock-Out Threshold')}
                    value={input.scheduledShiftEarlyClockOutThresholdMinutes}
                    onChange={(v) => setInput(i => ({ ...i, scheduledShiftEarlyClockOutThresholdMinutes: v ?? 5 }))}
                    suffix={t('minutes')}
                    min={0}
                  />
                </div>
              </section>

              {/* Section: Face Recognition */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Face Recognition')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <CheckField
                    label={t('Face Recognition Enabled')}
                    checked={input.faceRecognitionEnabled}
                    onChange={(v) => setInput(i => ({ ...i, faceRecognitionEnabled: v }))}
                  />
                  {input.faceRecognitionEnabled && (
                    <EnumSelect
                      label={t('Face Recognition Rule')}
                      enumType={FaceRecognitionConfig}
                      value={input.faceRecognitionConfig}
                      onChange={(v) => setInput(i => ({ ...i, faceRecognitionConfig: v as FaceRecognitionConfig }))}
                      t={t}
                    />
                  )}
                </div>
              </section>

              {/* Section: Clocking & Timesheet */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Clocking & Timesheet')}</h3>
                {/* "Timesheet Creation Day" was removed with the timesheet APPROVAL feature — its
                    only consumer was the weekly timesheet-creation Quartz job, which no longer runs.
                    The column is intentionally still persisted (no destructive migration); it is
                    simply no longer configurable, because configuring it did nothing. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <CheckField
                    label={t('Hide Clocking Images From Timesheet')}
                    checked={input.hideClockingImagesFromTimesheet}
                    onChange={(v) => setInput(i => ({ ...i, hideClockingImagesFromTimesheet: v }))}
                  />
                </div>
              </section>

              {/* Section: Notifications */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('Notifications')}</h3>
                <CheckField
                  label={t('Disable All Notifications')}
                  checked={input.disableAllNotifications}
                  onChange={(v) => setInput(i => ({ ...i, disableAllNotifications: v }))}
                />
              </section>

              {/* Section: AI Configuration */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('AI Configuration')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CheckField
                    label={t('AI Features Enabled')}
                    checked={input.aiFeaturesEnabled ?? rulesView?.aiFeaturesEnabled ?? false}
                    onChange={(v) => setInput(i => ({ ...i, aiFeaturesEnabled: v }))}
                  />
                  {rulesView?.hasCustomAIKey && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                      {t('Custom AI key is configured')}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('Azure OpenAI Endpoint')}</label>
                    <Input
                      value={input.azureOpenAIEndpoint ?? rulesView?.azureOpenAIEndpoint ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInput(i => ({ ...i, azureOpenAIEndpoint: e.target.value || null }))
                      }
                      placeholder={t('https://your-resource.openai.azure.com/', { nsSeparator: false })}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('Azure OpenAI Chat Deployment')}</label>
                    <Input
                      value={input.azureOpenAIChatDeployment ?? rulesView?.azureOpenAIChatDeployment ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInput(i => ({ ...i, azureOpenAIChatDeployment: e.target.value || null }))
                      }
                      placeholder={t('Deployment name')}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-foreground">{t('Azure OpenAI API Key')}</label>
                    <Input
                      type="password"
                      value={input.azureOpenAIApiKey ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setInput(i => ({ ...i, azureOpenAIApiKey: e.target.value || null }))
                      }
                      placeholder={t('Enter key to update, leave blank to keep existing')}
                    />
                  </div>
                  <div className="flex items-end">
                    <CheckField
                      label={t('Clear AI Key')}
                      checked={input.clearAIKey ?? false}
                      onChange={(v) => setInput(i => ({ ...i, clearAIKey: v || null }))}
                    />
                  </div>
                </div>
              </section>

              {/* Section: GPS Vehicle Tracking (Wialon) */}
              <section className="flex flex-col gap-4">
                <h3 className="text-base font-semibold text-foreground border-b border-border pb-2">{t('GPS Vehicle Tracking')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CheckField
                    label={t('Wialon Enabled')}
                    checked={input.wialonEnabled ?? false}
                    onChange={(v) => setInput(i => ({ ...i, wialonEnabled: v }))}
                  />
                  {rulesView?.hasWialonToken && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                      {t('Wialon token is configured')}
                    </div>
                  )}
                </div>
                {(input.wialonEnabled ?? false) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-sm font-medium text-foreground">{t('Wialon Token')}</label>
                      <Input
                        type="password"
                        value={input.wialonToken ?? ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInput(i => ({ ...i, wialonToken: e.target.value || null }))
                        }
                        placeholder={t('Enter token to update, leave blank to keep existing')}
                      />
                    </div>
                    <div className="flex items-end">
                      <CheckField
                        label={t('Clear Wialon Token')}
                        checked={input.clearWialonToken ?? false}
                        onChange={(v) => setInput(i => ({ ...i, clearWialonToken: v || null }))}
                      />
                    </div>
                  </div>
                )}
              </section>

              <hr className="border-border" />

              {/* Save Button */}
              <div className="flex justify-end pb-4">
                <Button
                  onClick={saving ? undefined : submit}
                  disabled={saving}
                  loading={saving}
                >
                  {t('Save Changes')}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
      </div>
    </PageLayout>
  );
}
